import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  ProfileModuleDto,
  ProfileModuleType,
} from '@up2stay/up2promo-api-client';
import type { ProfileTemplateFieldMode } from '@up2stay/up2promo-api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@up2stay/up2promo-ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@up2stay/up2promo-ui';
import { ConfirmDialog } from '@up2stay/up2promo-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { cn, Input, Switch } from '@up2stay/up2promo-ui';
import {
  Check,
  ChevronDown,
  GripVertical,
  MoreVertical,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { useRef, useState } from 'react';

import {
  PROFILE_EDIT_SECTION_CARD_CONTENT_CLASS,
  PROFILE_EDIT_SECTION_CARD_HEADER_CLASS,
  PROFILE_EDIT_SECTION_MUTED_CLASS,
  PROFILE_EDIT_SECTION_TITLE_CLASS,
} from '@/core/components/profile/edit/profile-edit-surface';
import { TemplateCustomizationHint } from '@/core/components/profile-templates/template-customization-hint';
import type { TemplateMemberValuesEditorUiProps } from '@/core/components/profile-templates/template-policy-member-controls';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  getModuleLabel,
  getModuleSectionTitle,
  MODULE_TYPE_LABELS,
} from '@/core/lib/module-labels';
import {
  getModuleId,
  getTemplateModuleOverrideDisplay,
  isPerEntryListModuleType,
  type TemplateEntryFieldLabels,
} from '@/core/lib/profile-module-entry-policies';
import { useDictionary } from '@/core/providers/dictionary-provider';

import { openIndexAfterMove, openIndexAfterRemove } from './open-module-index';
import { ModuleConfigWrapper } from './module-config-wrapper';
import {
  SortableModuleItem,
  type SortableModuleItemProps,
} from '@/core/components/profile/sortable-module-item';
import type { ProfileModulesSectionProps } from '@/core/components/profile/profile-modules-section-types';

const AVAILABLE_MODULE_TYPES = MODULE_TYPE_LABELS;

export function ProfileModulesSection({
  modules,
  onModulesChange,
  isWorkspaceProfile = false,
  readOnly = false,
  structureReadOnly = false,
  templateModules,
  showEntryPolicies = false,
  onModuleEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  isModuleCustomized,
  onResetModuleToTemplate,
  templateOverrideLabels,
  overrideHintMessage,
  showEntryOverrideHints: showEntryOverrideHintsProp,
  templateTeamIds,
  showManageTeamLink = false,
  workspaceId,
  templateId,
  publishStatus,
  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: ProfileModulesSectionProps) {
  const showEntryOverrideHints =
    showEntryOverrideHintsProp ??
    Boolean(templateModules?.length && !showEntryPolicies && !readOnly);
  const blockStructure = readOnly || structureReadOnly;
  const dict = useDictionary();
  type ModulesDict = SortableModuleItemProps['dict'] & {
    title?: string;
    description?: string;
    descriptionTemplate?: string;
    add?: string;
    addDialogTitle?: string;
    emptyTitle?: string;
    emptyHint?: string;
    lockedByTemplate?: string;
  };
  const editDict = (dict.profiles as { edit?: { modules?: ModulesDict } })
    ?.edit;
  const modulesDict = (editDict?.modules ?? {
    reorder: 'Reihenfolge ändern',
    sectionTitleLabel: 'Abschnittsüberschrift',
    resetToDefault: 'Auf Standard zurücksetzen',
    reset: 'Zurücksetzen',
    confirm: 'Übernehmen',
    collapse: 'Einklappen',
    expand: 'Aufklappen',
    editSectionTitle: 'Abschnittsüberschrift bearbeiten',
    moduleOptions: 'Optionen für Modul „{title}“',
    moduleEnable: 'Modul {title} aktivieren',
    moduleDisable: 'Modul {title} deaktivieren',
    moduleVisible: 'Modul anzeigen',
    removeModule: 'Modul entfernen',
    removeConfirmTitle: 'Modul „{title}“ entfernen?',
    removeConfirmDescription:
      'Alle Inhalte dieses Moduls werden gelöscht. Das lässt sich nicht rückgängig machen.',
    removeConfirmAction: 'Entfernen',
    removeConfirmCancel: 'Abbrechen',
  }) as SortableModuleItemProps['dict'];
  const m = editDict?.modules;
  const [addModuleOpen, setAddModuleOpen] = useState(false);
  /** Accordion: only one module expanded at a time */
  const [openModuleIndex, setOpenModuleIndex] = useState<number | null>(null);

  const handleAddModule = (type: ProfileModuleType) => {
    if (blockStructure) return;
    const newModule: ProfileModuleDto = {
      type,
      order: modules.length,
      enabled: true,
      config:
        type === 'team_members'
          ? {
              members: [],
              layout: 'grid',
              columns: 3,
              showRoles: true,
              showBios: false,
              showContactInfo: false,
              showProfileLinks: true,
            }
          : {},
    };

    onModulesChange([...modules, newModule]);
    setAddModuleOpen(false);
    setOpenModuleIndex(modules.length);
  };

  const handleRemoveModule = (index: number) => {
    if (blockStructure) return;
    const newModules = modules.filter((_, i) => i !== index);
    // Reorder modules
    const reorderedModules = newModules.map((module, i) => ({
      ...module,
      order: i,
    }));
    onModulesChange(reorderedModules);
    setOpenModuleIndex((open) => openIndexAfterRemove(open, index));
  };

  const handleToggleModule = (index: number) => {
    if (readOnly) return;
    const newModules = [...modules];
    const current = newModules[index];
    if (!current?.type) return;
    newModules[index] = {
      ...current,
      enabled: !current.enabled,
    };
    onModulesChange(newModules);
  };

  const handleConfigChange = (
    index: number,
    config: Record<string, unknown>,
  ) => {
    if (readOnly) return;
    const newModules = [...modules];
    const current = newModules[index];
    if (!current?.type) return;
    newModules[index] = {
      ...current,
      config,
    };
    onModulesChange(newModules);
  };

  const handleSectionTitleChange = (
    index: number,
    value: string | undefined,
  ) => {
    if (readOnly) return;
    const newModules = [...modules];
    const current = newModules[index];
    if (!current?.type) return;
    const prevConfig = current.config ?? {};
    const newConfig = { ...prevConfig };
    if (value === undefined || value === '') {
      delete newConfig.sectionTitle;
    } else {
      newConfig.sectionTitle = value;
    }
    newModules[index] = {
      ...current,
      config: newConfig,
    };
    onModulesChange(newModules);
  };

  const handleMoveModule = (fromIndex: number, toIndex: number) => {
    if (blockStructure) return;
    const newModules = [...modules];
    const [moved] = newModules.splice(fromIndex, 1);
    if (!moved) return;
    newModules.splice(toIndex, 0, moved);
    // Reorder modules
    const reorderedModules = newModules.map((module, i) => ({
      ...module,
      order: i,
    }));
    onModulesChange(reorderedModules);
    setOpenModuleIndex((open) => openIndexAfterMove(open, fromIndex, toIndex));
  };

  const sensors = useDndProfileSensors();
  const lastOverIdRef = useRef<string | number | null>(null);

  const handleDragStart = (_event: DragStartEvent) => {
    setOpenModuleIndex(null);
    lastOverIdRef.current = null;
    lockBodyScrollDuringDrag();
    void triggerDragStartHaptic();
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over == null) {
      lastOverIdRef.current = null;
      return;
    }
    const isNewSlot = lastOverIdRef.current !== over.id;
    const isBackToOwnSlot =
      over.id === active.id && lastOverIdRef.current === null;
    if (isNewSlot && !isBackToOwnSlot) {
      void triggerDragOverHaptic();
    }
    lastOverIdRef.current = over.id;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    unlockBodyScrollAfterDrag();
    void triggerDragEndHaptic();
    const { active, over } = event;
    if (over == null || active.id === over.id) return;
    const fromIndex = Number(active.id);
    const toIndex = Number(over.id);
    if (Number.isNaN(fromIndex) || Number.isNaN(toIndex)) return;
    handleMoveModule(fromIndex, toIndex);
  };

  const getUsedModuleTypes = () => {
    return new Set(modules.map((m) => m.type));
  };

  const availableToAdd = AVAILABLE_MODULE_TYPES.filter((m) => {
    if (getUsedModuleTypes().has(m.type)) return false;
    // Teammitglieder only for workspace profiles
    if (m.type === 'team_members' && !isWorkspaceProfile) return false;
    return true;
  });

  return (
    <div className="flex min-w-0 w-full flex-col gap-4 md:gap-5">
      <Card className="min-w-0 w-full">
        <CardHeader
          className={`min-w-0 ${PROFILE_EDIT_SECTION_CARD_HEADER_CLASS}`}
        >
          <div>
            <CardTitle className={PROFILE_EDIT_SECTION_TITLE_CLASS}>
              {m?.title ?? 'Module'}
            </CardTitle>
            <CardDescription className={PROFILE_EDIT_SECTION_MUTED_CLASS}>
              {blockStructure
                ? (m?.lockedByTemplate ??
                  fieldLockedHintMessage ??
                  'Module werden durch die Profilvorlage verwaltet.')
                : showEntryPolicies
                  ? m?.descriptionTemplate
                  : (m?.description ??
                    'Verwalte die Module deines Profils. Aktiviere oder deaktiviere Module und ändere ihre Reihenfolge.')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent
          className={`min-w-0 space-y-4 ${PROFILE_EDIT_SECTION_CARD_CONTENT_CLASS}`}
        >
          {modules.length === 0 ? (
            !blockStructure &&
            availableToAdd.length > 0 && (
              <Collapsible
                open={addModuleOpen}
                onOpenChange={setAddModuleOpen}
                className="space-y-2"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    aria-label={m?.add ?? 'Hinzufügen'}
                    className={cn(
                      'group flex w-full min-h-10 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 p-3',
                      'text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary',
                    )}
                  >
                    <Plus className="h-5 w-5" />
                    <span className="font-medium">
                      {m?.add ?? 'Hinzufügen'}
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        addModuleOpen && 'rotate-180',
                      )}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-2 pt-2">
                    {availableToAdd.map((moduleType) => (
                      <button
                        key={moduleType.type}
                        type="button"
                        onClick={() => handleAddModule(moduleType.type)}
                        className="w-full text-left p-3 border border-border rounded-lg hover:bg-accent transition-colors"
                      >
                        <div className="font-medium">{moduleType.label}</div>
                        <div className="text-sm text-muted-foreground">
                          {moduleType.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )
          ) : (
            <div className="space-y-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={modules.map((_, i) => String(i))}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {modules.map((module, index) => (
                      <SortableModuleItem
                        key={index}
                        module={module}
                        index={index}
                        isOpen={openModuleIndex === index}
                        onOpenChange={(open) =>
                          setOpenModuleIndex(open ? index : null)
                        }
                        onToggle={() => handleToggleModule(index)}
                        onConfigChange={(config) =>
                          handleConfigChange(index, config)
                        }
                        onSectionTitleChange={(value) =>
                          handleSectionTitleChange(index, value)
                        }
                        onRemove={() => handleRemoveModule(index)}
                        dict={modulesDict}
                        readOnly={readOnly}
                        structureReadOnly={structureReadOnly}
                        templateModules={templateModules}
                        showEntryPolicies={showEntryPolicies}
                        onModuleEntryPolicyChange={onModuleEntryPolicyChange}
                        policyLabels={policyLabels}
                        fieldLockedHintMessage={fieldLockedHintMessage}
                        isModuleCustomized={isModuleCustomized}
                        onResetModuleToTemplate={onResetModuleToTemplate}
                        templateOverrideLabels={templateOverrideLabels}
                        overrideHintMessage={overrideHintMessage}
                        showEntryOverrideHints={showEntryOverrideHints}
                        templateTeamIds={templateTeamIds}
                        showManageTeamLink={showManageTeamLink}
                        workspaceId={workspaceId}
                        templateId={templateId}
                        publishStatus={publishStatus}
                        memberRoster={memberRoster}
                        memberRosterLoading={memberRosterLoading}
                        hasAssignedTeams={hasAssignedTeams}
                        memberValuesDict={memberValuesDict}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {!blockStructure && availableToAdd.length > 0 && (
                <Collapsible
                  open={addModuleOpen}
                  onOpenChange={setAddModuleOpen}
                  className="space-y-2"
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      aria-label={m?.add ?? 'Hinzufügen'}
                      className={cn(
                        'group flex w-full min-h-10 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 p-3',
                        'text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary',
                      )}
                    >
                      <Plus className="h-5 w-5" />
                      <span className="font-medium">
                        {m?.add ?? 'Hinzufügen'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 transition-transform',
                          addModuleOpen && 'rotate-180',
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-2 pt-2">
                      {availableToAdd.map((moduleType) => (
                        <button
                          key={moduleType.type}
                          type="button"
                          onClick={() => handleAddModule(moduleType.type)}
                          className="w-full text-left p-3 border border-border rounded-lg hover:bg-accent transition-colors"
                        >
                          <div className="font-medium">{moduleType.label}</div>
                          <div className="text-sm text-muted-foreground">
                            {moduleType.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
